import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Select, Input, Button, Popconfirm, Spin } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';

dayjs.extend(customParseFormat);

const { Option } = Select;
const API_URL = process.env.REACT_APP_API_URL || '';

const ScheduleEntryModal = ({
    visible, onClose, onSave, onRemove, allFacultyAccounts, allFacultyProfiles,
    availableRooms, loadingRooms, initialData, subjectsList, gradeLevel,
    currentScheduleEntries,
    currentScheduleId,
    academicYear,
    academicTerm
}) => {
    const [form] = Form.useForm();
    const isEditing = initialData?.index !== undefined;
    const [isChecking, setIsChecking] = useState(false);

    const eligibleFaculty = useMemo(() => {
        if (!initialData?.subjectFormField || !gradeLevel || !allFacultyProfiles || !allFacultyAccounts) return [];
        const currentSubject = subjectsList.find(s => s.subjectCode === initialData.subjectFormField);
        if (!currentSubject) return [];
        const subjectClassification = currentSubject.classification;
        const eligibleProfileRefs = allFacultyProfiles
            .filter(profile => 
                profile.gradeLevelHandled?.includes(gradeLevel) &&
                profile.subjectClassificationHandled?.includes(subjectClassification)
            )
            .map(profile => profile.facultyRef?._id || profile.facultyRef);
        return (allFacultyAccounts || []).filter(account => 
            eligibleProfileRefs.map(String).includes(String(account._id))
        );
    }, [initialData?.subjectFormField, gradeLevel, allFacultyProfiles, allFacultyAccounts, subjectsList]);

    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                subjectFormField: initialData?.subjectFormField,
                room: initialData?.room || undefined,
                days: initialData?.days || [],
                timeStart: initialData?.timeStart || '07:00',
                timeEnd: initialData?.timeEnd || '08:00',
                facultyRef: initialData?.facultyRef || undefined,
            });
            if (isEditing && initialData.facultyRef) {
                const isStillEligible = eligibleFaculty.some(f => f._id === initialData.facultyRef);
                if (!isStillEligible) {
                    form.setFieldsValue({ facultyRef: undefined });
                    toast.warn("Previously assigned faculty is no longer eligible and has been cleared.");
                }
            }
        }
    }, [visible, initialData, form, eligibleFaculty, isEditing]);

    // This function now contains all conflict logic and is called manually.
    const checkConflicts = async (formValues) => {
        const { days, timeStart, timeEnd, room, facultyRef } = formValues;

        // 1. Local check (within the same schedule being edited)
        for (let i = 0; i < currentScheduleEntries.length; i++) {
            if (isEditing && i === initialData.index) continue; // Skip self when editing
            const entry = currentScheduleEntries[i];
            if (entry.days.some(day => days.includes(day)) && entry.timeStart < timeEnd && entry.timeEnd > timeStart) {
                if (room && entry.room === room) throw new Error(`Room conflict with ${entry.subjectFormField}.`);
                if (facultyRef && entry.facultyRef === facultyRef) throw new Error(`Faculty conflict with ${entry.subjectFormField}.`);
            }
        }
        
        // 2. API check (against other schedules in the DB)
        try {
            const response = await fetch(`${API_URL}/api/admin/class-schedules/check-conflict`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    academicYear, academicTerm, days, timeStart, timeEnd, room, facultyRef,
                    currentScheduleId: isEditing ? currentScheduleId : null,
                }),
            });
            const result = await response.json();
            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Conflict check service failed.');
            }
            const { conflicts } = result;
            const errorMessages = [...(conflicts.room || []), ...(conflicts.faculty || [])];
            if (errorMessages.length > 0) {
                throw new Error(errorMessages.join(' '));
            }
        } catch (error) {
            console.error("Conflict Check Error:", error);
            throw error; // Re-throw to be caught by handleOk
        }
    };

    const handleOk = async () => {
        setIsChecking(true);
        try {
            // Step 1: Validate required fields and format first
            const values = await form.validateFields();
            
            // Step 2: Manually run our conflict check
            await checkConflicts(values);

            // Step 3: If all checks pass, call onSave to update the parent form's state
            onSave({ ...initialData, ...values });

        } catch (error) {
            // This will catch errors from both validateFields and checkConflicts
            if (error.errorFields) {
                // This is a standard validation error (e.g., required field)
                // Ant Design already highlights the fields, so we just log it.
                console.log('Standard validation failed:', error.errorFields);
            } else {
                // This is our custom conflict error, so we show a toast.
                console.error('Conflict found:', error.message);
                toast.error(error.message);
            }
        } finally {
            setIsChecking(false);
        }
    };

    return (
        <Modal
            title={`${isEditing ? 'Edit' : 'Add'} Schedule Entry for ${initialData?.subjectFormField}`}
            open={visible}
            onCancel={onClose}
            footer={[
                isEditing && (
                    <Popconfirm
                        key="remove"
                        title="Are you sure?"
                        onConfirm={() => onRemove(initialData.index)}
                        okText="Yes, Remove"
                        cancelText="No"
                    >
                        <Button danger type="dashed" style={{ float: 'left' }}>Remove</Button>
                    </Popconfirm>
                ),
                <Button key="back" onClick={onClose} disabled={isChecking}>Cancel</Button>,
                <Button key="submit" type="primary" onClick={handleOk} loading={isChecking}>
                    {isChecking ? 'Checking...' : (isEditing ? 'Update Entry' : 'Add to Schedule')}
                </Button>,
            ]}
        >
            <Spin spinning={isChecking} tip="Checking for conflicts...">
                <Form form={form} layout="vertical">
                    <Form.Item label="Room" name="room" rules={[{ required: true, message: 'Room is required' }]}>
                        <Select
                            placeholder="Select an available room"
                            loading={loadingRooms}
                            showSearch
                            filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                        >
                            {(availableRooms || []).map(r => (<Option key={r._id} value={r.name}>{r.name}</Option>))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Faculty (Optional)" name="facultyRef">
                        <Select placeholder="Select an eligible faculty" allowClear showSearch>
                            {(eligibleFaculty || []).map(f => <Option key={f._id} value={f._id}>{`${f.firstName} ${f.lastName}`}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Days" name="days" rules={[{ required: true, message: 'At least one day is required' }]}>
                        <Select mode="multiple" placeholder="Select days">
                            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(d => <Option key={d} value={d}>{d}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Time Start" name="timeStart" rules={[{ required: true, message: 'Start time is required' }]}>
                        <Input type="time" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label="Time End"
                        name="timeEnd"
                        dependencies={['timeStart']}
                        rules={[
                            { required: true, message: 'End time is required' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    const startTime = getFieldValue('timeStart');
                                    if (!value || !startTime || dayjs(value, 'HH:mm').isAfter(dayjs(startTime, 'HH:mm'))) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('End time must be after start time'));
                                },
                            }),
                        ]}
                    >
                        <Input type="time" style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};

export default ScheduleEntryModal;